
import React, { useState, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Geolocation } from '@capacitor/geolocation';
import Layout from './Layout';
import CommandCenter from './CommandCenter';
import PulseFeedback from './PulseFeedback';
import GuardianAngel from './GuardianAngel';
import IntelCortex from './IntelCortex';
import ReportHazard from './ReportHazard';
import Dashboard from './Dashboard';
import SOSOverlay from './SOSOverlay';
import Onboarding from './Onboarding';
import InitializationScreen from './InitializationScreen';
import ContactPage from './ContactPage';
import AuthPage from './AuthPage';
import AlertSystem from './AlertSystem';
import ProfilePage from './ProfilePage';
import ContactRequestsNotification from './ContactRequestsNotification';
import ChatNotification from './ChatNotification';
import ChatInterface from './ChatInterface';
import MessengerPage from './MessengerPage';
import PulsePage from './PulsePage';
import HazardDetailOverlay from './HazardDetailOverlay';
import IntelFeedPage from './IntelFeedPage';
import NewsDetailOverlay from './NewsDetailOverlay';


import { ViewState, LatLng, Alert, Language, BeaconState, HazardType, WeatherData, NewsItem, Contact, SafetyStatus, IncomingRequest } from '../types';
import { getCurrentPosition, getCyberLocationName } from '../services/locationService';
import { updateMyLocation } from '../services/squadService';
import { fetchRealHazards } from '../services/disasterService';
import { startSeismicStream, stopSeismicStream } from '../services/seismicSocketService';
import { fetchTrafficIncidents } from '../services/trafficService';
import { syncGlobalHazards, getVerifiedHazards } from '../services/hazardSyncService';
import { fetchRegionalIntelligence } from '../services/intelligenceService';
import { getWeather } from '../services/weatherService';
import { getSimulationNews } from '../services/newsService';
import { contactService } from '../services/contactService';
import { soundService } from '../services/soundService';

const ConsoleApp: React.FC = () => {
    // --- STATE ---
    const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(() => localStorage.getItem('has_onboarded') === 'true');
    const [isSystemReady, setIsSystemReady] = useState(false);
    const [isRestoringSession, setIsRestoringSession] = useState(true);
    const [initStatusText, setInitStatusText] = useState("INITIALIZING...");
    const [pulseFeedback, setPulseFeedback] = useState<{ senderId: string, senderName: string, status: string } | null>(null);

    // --- SESSION CHECK ON MOUNT ---
    useEffect(() => {
        const storedId = localStorage.getItem('squad_id');
        if (!storedId) {
            setIsRestoringSession(false);
        }
    }, []);

    // DEBUG FUNCTION
    (window as any).testLocationSave = async () => {
        const userId = localStorage.getItem('squad_id');
        console.log('🔍 Testing location save for user:', userId);
        if (!userId) {
            console.error('❌ No squad_id in localStorage');
            return;
        }

        console.log('📡 Getting GPS position...');
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                console.log('✅ GPS Position:', pos.coords.latitude, pos.coords.longitude);
                const { supabase } = await import('../services/supabase');
                const { data, error } = await supabase
                    .from('profiles')
                    .update({
                        location_lat: pos.coords.latitude,
                        location_lng: pos.coords.longitude,
                        updated_at: new Date().toISOString()
                    })
                    .eq('id', userId)
                    .select();

                if (error) console.error('❌ Supabase error:', error);
                else console.log('✅ Location saved:', data?.[0]);
            },
            (err) => console.error('❌ GPS Error:', err.message),
            { enableHighAccuracy: true, timeout: 15000 }
        );
    };

    const [currentView, setView] = useState<ViewState>(ViewState.DASHBOARD);
    const [lang, setLang] = useState<Language>('en');

    // Modals
    const [showReportModal, setShowReportModal] = useState(false);
    const [showPulseMenu, setShowPulseMenu] = useState(false);
    const [showGuardianAngel, setShowGuardianAngel] = useState(false);

    // Data
    const [focusedLocation, setFocusedLocation] = useState<LatLng | null>(null);
    const [customHazards, setCustomHazards] = useState<Alert[]>([]);
    const [activeSOS, setActiveSOS] = useState<Alert | null>(null);
    const [acknowledgedAlerts, setAcknowledgedAlerts] = useState<string[]>([]);
    const [selectedHazard, setSelectedHazard] = useState<Alert | null>(null);
    const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null);
    const [viewingProfileId, setViewingProfileId] = useState<string | null>(null);

    // Navigation Params
    const [navParams, setNavParams] = useState<any>(null);

    // Contacts & Profile
    const [userProfile, setUserProfile] = useState<Contact | null>(null);
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [nearbyUsers, setNearbyUsers] = useState<Contact[]>([]);
    const [activeChatContact, setActiveChatContact] = useState<Contact | null>(null);
    const [incomingPulse, setIncomingPulse] = useState<IncomingRequest | null>(null);

    // The "Truth Source"
    const [initialData, setInitialData] = useState<{
        location: LatLng;
        weather: WeatherData;
        news: NewsItem[];
        hazards: Alert[];
    } | null>(null);

    // Global News State
    const [globalNews, setGlobalNews] = useState<NewsItem[]>([]);

    useEffect(() => {
        if (initialData?.news) {
            setGlobalNews(initialData.news);
        }
    }, [initialData?.news]);

    // Beacon
    const [beacon, setBeacon] = useState<BeaconState>({ active: false, type: null, startTime: 0, lastBroadcast: '' });

    // Handle RTL
    useEffect(() => {
        document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
        document.documentElement.lang = lang;
    }, [lang]);

    // --- HELPERS ---
    const checkCriticalHazards = (hazards: Alert[]) => { };

    // --- BOOT SEQUENCE ---
    const getCurrentPosition = async (): Promise<LatLng> => {
        try {
            // Check and Request Permissions
            const permission = await Geolocation.checkPermissions();
            if (permission.location !== 'granted') {
                const request = await Geolocation.requestPermissions();
                if (request.location !== 'granted') {
                    throw new Error("Location permission denied");
                }
            }

            // Get Position
            const coordinates = await Geolocation.getCurrentPosition({
                enableHighAccuracy: true,
                timeout: 15000,
                maximumAge: 0
            });
            return { lat: coordinates.coords.latitude, lng: coordinates.coords.longitude };
        } catch (e) {
            console.warn("Native Geolocation failed, falling back to Web API", e);
            return new Promise((resolve, reject) => {
                if (!navigator.geolocation) {
                    reject(new Error("Geolocation not supported"));
                    return;
                }
                navigator.geolocation.getCurrentPosition(
                    (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
                    (err) => reject(err),
                    { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
                );
            });
        }
    };

    const handleOnboardingComplete = async () => {
        localStorage.setItem('has_onboarded', 'true');
        setHasCompletedOnboarding(true);
    };

    const initializeSystem = async () => {
        const MAX_BOOT_TIME = 15000;

        const bootTask = async () => {
            try {
                setInitStatusText("TRIANGULATING POSITION...");
                const pos = await getCurrentPosition();

                setInitStatusText("ESTABLISHING MCP SECURE UPLINK...");
                try {
                    const { mcpService } = await import('../services/mcpService');
                    await mcpService.initialize();
                } catch (mcpError) {
                    console.warn('⚠️ MCP init failed (non-critical):', mcpError);
                }

                setInitStatusText("FETCHING GLOBAL INTELLIGENCE & NEWS DATA...");
                syncGlobalHazards(pos).catch(err => console.warn('Background sync warning:', err));

                setInterval(() => {
                    syncGlobalHazards(pos).catch(err => console.warn('Periodic sync warning:', err));
                }, 300000);

                const isNorth = pos.lat > 34.0;
                const scanRadius = isNorth ? 100 : 50;

                const [weather, intel, trafficHazards] = await Promise.all([
                    getWeather(pos, lang).catch(() => null),
                    fetchRegionalIntelligence(pos, scanRadius, lang).catch(() => ({ hazards: [], news: [] })),
                    fetchTrafficIncidents(pos).catch(() => [])
                ]);

                const allHazards = [...(intel.hazards || []), ...trafficHazards];
                const news = intel.news || [];
                const safeWeather = weather || { temp: 0, condition: 'OFFLINE', humidity: 0, windSpeed: 0, locationName: 'Unknown', isNight: true };

                setInitStatusText("CONNECTING TO SQUAD NET...");
                const storedId = localStorage.getItem('squad_id');
                let profile = null;
                let myContacts: Contact[] = [];

                if (storedId) {
                    profile = await contactService.ensureProfile(storedId, "Returning Agent");
                    if (profile) {
                        myContacts = await contactService.getContacts(storedId);
                        updateMyLocation(pos.lat, pos.lng, 100);
                    }
                }

                let nearby: Contact[] = [];
                if (profile) {
                    try { nearby = await contactService.getAllNearbyUsers(profile.id, pos); } catch (e) { }
                }

                return { pos, weather: safeWeather, news, hazards: allHazards, profile, contacts: myContacts, nearby };

            } catch (e) {
                console.error("Boot Critical Failure", e);
                return null;
            }
        };

        try {
            const dataResult = await Promise.race([
                bootTask(),
                new Promise((_, reject) => setTimeout(() => reject("Timeout"), MAX_BOOT_TIME))
            ]) as any;

            if (dataResult) {
                setInitialData({
                    location: dataResult.pos,
                    weather: dataResult.weather,
                    news: dataResult.news || [],
                    hazards: dataResult.hazards || []
                });

                if (dataResult.profile) setUserProfile(dataResult.profile);
                if (dataResult.contacts) setContacts(dataResult.contacts);
                if (dataResult.nearby) setNearbyUsers(dataResult.nearby);

                checkCriticalHazards(dataResult.hazards || []);
            } else {
                console.warn("⚠️ Boot failed, initializing Safe Mode");
                const fallbackPos = { lat: 48.8566, lng: 2.3522 };
                const fallbackHazards = await fetchRealHazards(fallbackPos, false);

                setInitialData({
                    location: fallbackPos,
                    weather: { temp: 22, condition: 'Clear', humidity: 45, windSpeed: 10, locationName: 'Safe Mode Sector', isNight: true },
                    news: [],
                    hazards: fallbackHazards
                });

                const mockProfile: Contact = {
                    id: 'cmd-offline',
                    name: 'Commander (Offline)',
                    role: 'Commander',
                    status: SafetyStatus.SAFE,
                    battery: 85,
                    locationName: 'Paris Safe Zone',
                    coordinates: { lat: 48.8566, lng: 2.3522 },
                    lastSeen: new Date().toISOString(),
                    avatarUrl: 'https://i.pravatar.cc/300?u=cmd',
                    lastUpdated: Date.now(),
                    isTrackable: true
                };
                setUserProfile(mockProfile);
                setContacts([
                    {
                        id: 'sq-1', name: 'Sarah', role: 'Scout', status: SafetyStatus.SAFE, battery: 92, locationName: 'Sector 4',
                        coordinates: { lat: 48.858, lng: 2.354 }, lastSeen: new Date().toISOString(), isTrackable: true,
                        lastUpdated: Date.now(), avatarUrl: 'https://i.pravatar.cc/300?u=sarah'
                    }
                ]);
            }
        } catch (e) {
            console.error("Boot Error", e);
        } finally {
            setIsSystemReady(true);
            setIsRestoringSession(false);
        }
    };

    useEffect(() => {
        if (hasCompletedOnboarding && !isSystemReady) {
            initializeSystem();
            const timer = setTimeout(() => setIsSystemReady(true), 15000);
            return () => clearTimeout(timer);
        }
    }, [hasCompletedOnboarding]);

    // --- REFS FOR STALE CLOSURE FIX ---
    const contactsRef = React.useRef(contacts);
    const nearbyRef = React.useRef(nearbyUsers);

    useEffect(() => { contactsRef.current = contacts; }, [contacts]);
    useEffect(() => { nearbyRef.current = nearbyUsers; }, [nearbyUsers]);

    useEffect(() => {
        if (hasCompletedOnboarding) initializeSystem();
    }, [hasCompletedOnboarding]);

    useEffect(() => {
        if (!userProfile?.id) return;

        const params = new URLSearchParams(window.location.search);
        const addId = params.get('add');
        if (addId) {
            contactService.sendContactRequest(userProfile.id, addId).then((result) => {
                if (result.success) alert("CONTACT REQUEST SENT!");
                window.history.replaceState({}, document.title, window.location.pathname);
            });
        }

        const hazardSub = contactService.subscribeToHazards((payload) => {
            if (payload.new) setCustomHazards(prev => [payload.new, ...prev]);
        });

        const contactSub = contactService.subscribeToContacts(userProfile.id, async () => {
            const updated = await contactService.getContacts(userProfile.id);
            setContacts(updated);
        });

        const locationSub = contactService.subscribeToSquadLocations((payload) => {
            const updatedProfile = payload.new;
            if (!updatedProfile) return;
            setContacts(prev => prev.map(c => c.id === updatedProfile.id ? { ...c, status: updatedProfile.status, coordinates: { lat: updatedProfile.location_lat, lng: updatedProfile.location_lng } } : c));
        });

        const heartbeatInterval = setInterval(async () => {
            try {
                const pos = await getCurrentPosition();
                updateMyLocation(pos.lat, pos.lng, 100);
                setNearbyUsers([]);
            } catch (e) { }
        }, 30000);

        const messageSub = userProfile?.id ? contactService.subscribeToMessages(userProfile.id, (payload) => {
            const msg = payload.new;
            if (msg && msg.receiver_id === userProfile.id) {
                const currentContacts = contactsRef.current;
                const sender = [...currentContacts].find(c => c.id === msg.sender_id);
                const senderName = sender?.name || 'Unknown Agent';

                try {
                    if (msg.content === '::PULSE_REQ::' || msg.content === '::PULSE::') soundService.playPulseSound();
                    else if (msg.content.startsWith('::PULSE_ACK::')) soundService.playCheckSound();
                    else if (msg.content === '❤️') soundService.playLoveSound();
                    else if (msg.content.includes('DANGER') || msg.content.includes('⚠️')) soundService.playWarningSound();
                } catch (e) { }

                setContacts(prev => prev.map(c => {
                    if (c.id === msg.sender_id) {
                        return {
                            ...c,
                            lastMessage: msg.content,
                            lastMessageTime: Date.now(),
                            lastEmoji: (['❤️', '⚠️'].includes(msg.content) ? msg.content : c.lastEmoji),
                            unreadCount: activeChatContact?.id === c.id ? 0 : (c.unreadCount || 0) + 1
                        };
                    }
                    return c;
                }));

                if (msg.content === '::PULSE_REQ::' || msg.content === '::PULSE::') {
                    soundService.playPulseSound();
                    setView(ViewState.PULSE);
                    setIncomingPulse({
                        senderId: msg.sender_id,
                        senderName: sender?.name || 'Unknown',
                        timestamp: new Date()
                    });
                } else if (msg.content.startsWith('::PULSE::') || msg.content.startsWith('::PULSE_ACK::')) {
                    const statusVal = msg.content.split('::').pop() || 'OK';
                    setPulseFeedback({ senderId: msg.sender_id, senderName: senderName, status: statusVal });
                } else if (msg.content.includes('DANGER') || msg.content.includes('⚠️')) {
                    alert(`⚠️ WARNING from ${senderName}: ${msg.content}`);
                }

                if (sender && ['❤️', '⚠️', '📡', '✅'].includes(msg.content) || msg.content.startsWith('::')) {
                    const emoji = msg.content.startsWith('::PULSE') ? '📡' : msg.content === '❤️' ? '❤️' : '⚠️';
                    // simplified
                }
            }
        }) : null;

        return () => {
            hazardSub.unsubscribe();
            contactSub.unsubscribe();
            locationSub.unsubscribe();
            clearInterval(heartbeatInterval);
            messageSub?.unsubscribe();
        };

    }, [userProfile?.id]);


    // --- ACTIONS ---
    const [selectedContactId, setSelectedContactId] = useState<string | null>(null);

    const handleTrackMember = (loc: LatLng, contactId?: string) => {
        setFocusedLocation(loc);
        if (contactId) setSelectedContactId(contactId);
        setView(ViewState.COMMAND_CENTER);
        setShowPulseMenu(false);
    };

    const handleActivateBeacon = (type: HazardType) => {
        setBeacon({ active: true, type, startTime: Date.now(), lastBroadcast: 'Initializing...' });
        setShowReportModal(false);
        if (userProfile?.id && initialData?.location) {
            contactService.sendAlert({
                type: 'SOS', severity: 'critical', description: `EMERGENCY BEACON: ${type}`,
                location_lat: initialData.location.lat, location_lng: initialData.location.lng,
                reporter_id: userProfile.id
            });
        }
    };

    const handleDismissSOS = () => {
        if (activeSOS) {
            setAcknowledgedAlerts(prev => [...prev, activeSOS.id]);
            setActiveSOS(null);
        }
    };

    const handleSendPulse = async (contactIds: string[]) => {
        if (!userProfile) return;
        contactIds.forEach(id => contactService.sendMessage(userProfile.id, id, '::PULSE_REQ::'));
        soundService.playPulseSound();
    };

    useEffect(() => {
        if (hasCompletedOnboarding && 'geolocation' in navigator) {
            navigator.geolocation.getCurrentPosition(() => { }, (err) => alert("⚠️ GPS ACCESS REQUIRED"));
        }
    }, [hasCompletedOnboarding]);

    const handleMapRegionChange = async (center: LatLng) => {
        try {
            const intel = await fetchRegionalIntelligence(center, 50, lang);
            setCustomHazards(prev => {
                const existingIds = new Set(prev.map(h => h.id));
                const newHazards = intel.hazards.filter(h => !existingIds.has(h.id));
                return newHazards.length === 0 ? prev : [...newHazards, ...prev];
            });
            setGlobalNews(prev => {
                const existingIds = new Set(prev.map(n => n.id));
                const newNews = intel.news.filter(n => !existingIds.has(n.id));
                return [...newNews, ...prev].slice(0, 30);
            });
        } catch (e) {
            console.error("Region Sync Failed:", e);
        }
    };

    const handleTriggerSOS = async (alert: Alert, shouldBroadcast = true) => {
        setActiveSOS(alert);
        setCustomHazards(prev => [alert, ...prev]);
        if (shouldBroadcast) {
            try {
                await contactService.sendAlert({
                    type: 'SOS', severity: alert.severity, description: alert.description || `SOS: ${alert.type}`,
                    location_lat: alert.coordinates.lat, location_lng: alert.coordinates.lng,
                    reporter_id: userProfile?.id, location_name: alert.location
                });
            } catch (e) {
                console.error('Failed to broadcast SOS:', e);
            }
        }
    };

    // --- RENDER ---
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    const renderView = () => {
        if (showGuardianAngel) return <GuardianAngel />;

        return (
            <>
                <AnimatePresence>
                    {pulseFeedback && (
                        <PulseFeedback
                            senderName={pulseFeedback.senderName}
                            senderAvatar={`https://i.pravatar.cc/150?u=${pulseFeedback.senderId}`}
                            status={pulseFeedback.status}
                            onDismiss={() => setPulseFeedback(null)}
                        />
                    )}
                </AnimatePresence>

                {(() => {
                    switch (currentView) {
                        case ViewState.DASHBOARD:
                            return <Dashboard
                                userProfile={userProfile}
                                weather={initialData?.weather ?? null}
                                location={initialData?.location ?? null}
                                hazards={initialData?.hazards || []}
                                contacts={contacts}
                                onNavigate={(view, params) => {
                                    if (params) setNavParams(params);
                                    setView(view as ViewState);
                                }}
                                onSos={() => setShowReportModal(true)}
                                onNewsUpdate={setGlobalNews}
                            />;
                        case ViewState.INTEL_FEED:
                            return <IntelFeedPage
                                hazards={[...(initialData?.hazards || []), ...customHazards]}
                                onBack={() => setView(ViewState.COMMAND_CENTER)}
                                onSelectHazard={(h) => {
                                    setSelectedHazard(h);
                                    setView(ViewState.COMMAND_CENTER);
                                }}
                            />;
                        case ViewState.COMMAND_CENTER:
                            return <CommandCenter
                                userLocation={initialData?.location || null}
                                userProfile={userProfile}
                                contacts={contacts}
                                hazards={[...(initialData?.hazards || []), ...customHazards]}
                                onSos={() => setShowReportModal(true)}
                                onOpenMenu={() => setIsSidebarOpen(true)}
                                onRegionChange={handleMapRegionChange}
                                onSelectHazard={(h) => setSelectedHazard(h)}
                                onSelectNews={(n) => setSelectedNews(n)}
                                onOpenChat={(contactId) => {
                                    const contact = contacts.find(c => c.id === contactId);
                                    if (contact) setActiveChatContact(contact);
                                }}
                                onViewProfile={(contactId) => {
                                    setViewingProfileId(contactId);
                                    setView(ViewState.PROFILE);
                                }}
                                onSendPulse={(contactId) => handleSendPulse([contactId])}
                                onNavigate={(page) => {
                                    if (page === 'dashboard') setView(ViewState.DASHBOARD);
                                    else if (page === 'profile') setView(ViewState.PROFILE);
                                    else if (page === 'contacts') setView(ViewState.CONTACTS);
                                    else if (page === 'feed') setView(ViewState.INTEL_FEED);
                                    else if (page === 'pulse') setView(ViewState.PULSE);
                                }}
                                globalNews={globalNews}
                                initialFocus={navParams}
                            />;

                        case ViewState.INTEL_CORTEX:
                            return <IntelCortex initialContext={initialData} onBack={() => setView(ViewState.DASHBOARD)} />;

                        case ViewState.CONTACTS:
                            return <ContactPage
                                currentUser={userProfile}
                                contacts={contacts}
                                onOpenChat={(contact) => setActiveChatContact(contact)}
                                onTrackContact={(id) => {
                                    const contact = contacts.find(c => c.id === id);
                                    if (contact) handleTrackMember(contact.coordinates, id);
                                }}
                                onViewProfile={(id) => {
                                    setViewingProfileId(id);
                                    setView(ViewState.PROFILE);
                                }}
                                onAddContact={async (id) => {
                                    if (userProfile?.id) {
                                        const result = await contactService.sendContactRequest(userProfile.id, id);
                                        if (result.success) alert("Request Sent!");
                                    }
                                }}
                                onShare={() => { if (navigator.share) navigator.share({ title: 'Digital Guardian', url: window.location.href }); }}
                                onLogout={() => { contactService.logout(); window.location.reload(); }}
                                onOpenMessenger={() => setView(ViewState.MESSENGER)}
                                onRemoveContact={async (id) => {
                                    if (confirm('Remove contact?')) {
                                        await contactService.removeContact(userProfile?.id || '', id);
                                        window.location.reload();
                                    }
                                }}
                            />;

                        case ViewState.PROFILE:
                            const profileToShow = viewingProfileId ? contacts.find(c => c.id === viewingProfileId) || userProfile : userProfile;
                            if (!profileToShow) return <div>Loading Profile...</div>;
                            return <ProfilePage
                                profile={profileToShow}
                                isOwnProfile={!viewingProfileId || viewingProfileId === userProfile?.id}
                                onLogout={async () => { await contactService.logout(); window.location.reload(); }}
                                onBack={() => { setViewingProfileId(null); setView(ViewState.COMMAND_CENTER); }}
                            />;

                        case ViewState.MESSENGER:
                            return <MessengerPage
                                currentUser={userProfile!}
                                contacts={[...contacts, ...nearbyUsers]}
                                onBack={() => setView(ViewState.CONTACTS)}
                                onOpenChat={(contact) => setActiveChatContact(contact)}
                            />;

                        case ViewState.PULSE:
                            return <PulsePage
                                currentUser={userProfile!}
                                contacts={contacts}
                                onBack={() => setView(ViewState.COMMAND_CENTER)}
                                onSendPulse={(contactId) => handleSendPulse([contactId])}
                            />;

                        default:
                            return <Dashboard
                                userProfile={userProfile}
                                weather={initialData?.weather ?? null}
                                location={initialData?.location ?? null}
                                hazards={initialData?.hazards || []}
                                contacts={contacts}
                                onNavigate={(view) => setView(view as ViewState)}
                                onSos={() => setShowReportModal(true)}
                                onNewsUpdate={setGlobalNews}
                            />;
                    }
                })()}
            </>
        );
    };

    // --- EARLY RETURNS ---
    if (isRestoringSession) return <InitializationScreen statusMessage="RESTORING SECURE SESSION..." onForceEnter={() => setIsRestoringSession(false)} />;

    if (!userProfile) {
        return <AuthPage onLogin={(user) => {
            setUserProfile(user);
            localStorage.setItem('squad_id', user.id);
        }} />;
    }

    if (!hasCompletedOnboarding) return <Onboarding onComplete={handleOnboardingComplete} />;

    if (!isSystemReady) return <InitializationScreen statusMessage={initStatusText} onForceEnter={() => setIsSystemReady(true)} onComplete={() => setIsSystemReady(true)} />;

    return (
        <div className={`min-h-screen w-full bg-black text-white overflow-hidden ${lang === 'ar' ? 'font-[Cairo]' : 'font-[Rajdhani]'}`}>
            <Layout
                currentView={currentView}
                setView={(v) => { setShowGuardianAngel(false); setView(v); }}
                lang={lang} setLang={setLang}
                onOpenReport={() => setShowReportModal(true)}
                onOpenPulse={() => setView(ViewState.PULSE)}
                onOpenAngel={() => setShowGuardianAngel(true)}
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
            >
                {renderView()}
            </Layout>

            {/* OVERLAYS */}
            {showReportModal && <ReportHazard onClose={() => setShowReportModal(false)} onReport={(hazard) => { setCustomHazards(prev => [hazard, ...prev]); handleTriggerSOS(hazard, true); }} onActivateBeacon={handleActivateBeacon} lang={lang} />}
            {activeSOS && <SOSOverlay alert={activeSOS} onDismiss={handleDismissSOS} lang={lang} />}
            {selectedHazard && <HazardDetailOverlay hazard={selectedHazard} onClose={() => setSelectedHazard(null)} />}
            {selectedNews && <NewsDetailOverlay newsItem={selectedNews} onClose={() => setSelectedNews(null)} />}
        </div>
    );
};

export default ConsoleApp;
